import { PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from "react";

export const EDITOR_MIN_HEIGHT_PX = 80;

/** Pointer movement below this is a click (collapse), not a drag (resize). */
const DRAG_THRESHOLD_PX = 3;

const maxEditorHeight = () => Math.round(window.innerHeight * 0.6);

/**
 * Drag-to-resize for the composer textarea via the grip above the editor
 * (mockup `.se-grip`, `cursor: ns-resize`). Dragging up grows the editor
 * (it is anchored at the bottom); a click without movement falls through to
 * `onGripClick` (mockup: clicking the grip collapses the editor). While the
 * user hasn't dragged yet, `heightPx` is undefined and the textarea keeps
 * its content auto-resize.
 */
export const useEditorResize = ({ onGripClick }: { onGripClick: () => void }) => {
	const [heightPx, setHeightPx] = useState<number | undefined>(undefined);
	const dragStateRef = useRef<{ startY: number; startHeight: number; dragged: boolean } | null>(
		null
	);

	const handleGripPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLDivElement>) => {
			event.preventDefault();

			const grip = event.currentTarget;
			// Measure the composer textarea's current rendered height as the drag
			// baseline (works for both auto and already-fixed heights).
			const textarea = grip.closest("[data-editor-panel]")?.querySelector("textarea");
			const startHeight = heightPx ?? textarea?.getBoundingClientRect().height ?? 120;

			dragStateRef.current = { startY: event.clientY, startHeight, dragged: false };

			const handleMove = (moveEvent: PointerEvent) => {
				const state = dragStateRef.current;

				if (!state) {
					return;
				}

				const delta = state.startY - moveEvent.clientY;

				if (!state.dragged && Math.abs(delta) <= DRAG_THRESHOLD_PX) {
					return;
				}

				state.dragged = true;
				setHeightPx(
					Math.min(Math.max(state.startHeight + delta, EDITOR_MIN_HEIGHT_PX), maxEditorHeight())
				);
			};

			const handleUp = () => {
				const wasDrag = dragStateRef.current?.dragged;

				dragStateRef.current = null;
				window.removeEventListener("pointermove", handleMove);
				window.removeEventListener("pointerup", handleUp);

				if (!wasDrag) {
					onGripClick();
				}
			};

			window.addEventListener("pointermove", handleMove);
			window.addEventListener("pointerup", handleUp);
		},
		[heightPx, onGripClick]
	);

	return { heightPx, handleGripPointerDown };
};
